import { useEffect, useState } from "react";
// @ts-ignore
import "./NotificationsSection.scss";
import Switch from "../../../elements/switch/Switch";
import { COLORS } from "../../../models/colors";
import { bemElement } from "../../../utils/bem-class-names";
import { ListItem } from "../../list-item/ListItem";
import {
  ReminderSettingFragment,
  useReminderSettingsQuery
} from "./NotificationsSection.generated";
import { useUser } from "../../../providers/user";
import { useAxios } from "../../../providers/axios";
import { SortOrder } from "../../../types.generated";

const baseClassName = "notifications-section";
const bem = bemElement(baseClassName);

type ReminderType = "EMAIL" | "SMS" | "EMAIL_PROMO" | "SMS_PROMO";

interface IContactToggle {
  label: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}

const ContactToggle = ({ label, enabled, onChange }: IContactToggle) => {
  const control = (
    <Switch
      color={COLORS.PRIMARY}
      checked={enabled}
      onChange={() => onChange(!enabled)}
    />
  );

  return (
    <ListItem control={control} disableRightArrow className={bem("contact")}>
      <div className={bem("contact-value")}>{label}</div>
    </ListItem>
  );
};

const NotificationsSection = () => {
  const { user } = useUser();
  const { api } = useAxios();
  const { data, refetch } = useReminderSettingsQuery({
    variables: {
      where: {
        user_id: {
          equals: user.id
        }
      },
      orderBy: {
        type: SortOrder.Desc
      }
    },
    skip: !user.id
  });

  const [enabledByType, setEnabledByType] = useState<Record<string, boolean>>(
    {}
  );

  useEffect(() => {
    if (data?.reminderSettings) {
      const map: Record<string, boolean> = {};
      data.reminderSettings.forEach((setting: ReminderSettingFragment) => {
        map[setting.type] = setting.enabled;
      });
      setEnabledByType(map);
    }
  }, [data?.reminderSettings]);

  const hasEmail = !!user.email && user.email.trim().length > 0;
  const hasPhone = !!user.phone_number && user.phone_number.trim().length > 0;

  const updateSetting = async (type: ReminderType, enabled: boolean) => {
    const value =
      type === "EMAIL" || type === "EMAIL_PROMO"
        ? user.email
        : user.phone_number;
    const previous = !!enabledByType[type];

    // Optimistically reflect the toggle; revert if the request fails.
    setEnabledByType((prev) => ({ ...prev, [type]: enabled }));

    try {
      await api.put("/v1/users/reminder-settings", { type, value, enabled });
      // Sync with the server so the row we may have just created is reflected.
      refetch();
    } catch (error) {
      setEnabledByType((prev) => ({ ...prev, [type]: previous }));
    }
  };

  if (!user.id) {
    return null;
  }

  const reminderRows: {
    type: ReminderType;
    label: string;
    visible: boolean;
  }[] = [
    { type: "EMAIL", label: user.email, visible: hasEmail },
    { type: "SMS", label: user.phone_number, visible: hasPhone }
  ];
  const promoRows: { type: ReminderType; label: string; visible: boolean }[] = [
    { type: "EMAIL_PROMO", label: user.email, visible: hasEmail },
    { type: "SMS_PROMO", label: user.phone_number, visible: hasPhone }
  ];

  const hasReminderRows = reminderRows.some((row) => row.visible);
  const hasPromoRows = promoRows.some((row) => row.visible);

  if (!hasReminderRows && !hasPromoRows) {
    return null;
  }

  return (
    <div className={baseClassName}>
      {hasReminderRows && (
        <>
          <div className={bem("title")}>Contact Info:</div>
          <div className={bem("contacts")}>
            {reminderRows
              .filter((row) => row.visible)
              .map((row) => (
                <ContactToggle
                  key={row.type}
                  label={row.label}
                  enabled={!!enabledByType[row.type]}
                  onChange={(enabled) => updateSetting(row.type, enabled)}
                />
              ))}
          </div>
        </>
      )}
      {hasPromoRows && (
        <>
          <div className={bem("title")}>Promotional contact info:</div>
          <div className={bem("contacts")}>
            {promoRows
              .filter((row) => row.visible)
              .map((row) => (
                <ContactToggle
                  key={row.type}
                  label={row.label}
                  enabled={!!enabledByType[row.type]}
                  onChange={(enabled) => updateSetting(row.type, enabled)}
                />
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsSection;
